import { createBrowserRouter, RouterProvider, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Navbar } from './components/Navbar/Navbar';
import { Footer } from './components/Footer/Footer';
import { AdminLayout } from './components/AdminLayout/AdminLayout';
import { RequireAdmin } from './components/RequireAdmin/RequireAdmin';
import HomePage from './pages/HomePage';
import MenuPage from './pages/MenuPage';
import ProductDetailPage from './pages/ProductDetailPage';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import ForgotPasswordPage from './pages/admin/ForgotPasswordPage';
import ResetPasswordPage from './pages/admin/ResetPasswordPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminProductsPage from './pages/admin/AdminProductsPage';
import AdminAddProductPage from './pages/admin/AdminAddProductPage';
import AdminOrdersPage from './pages/admin/AdminOrdersPage';
import AdminTestimonialsPage from './pages/admin/AdminTestimonialsPage';
import AdminCategoriesPage from './pages/admin/AdminCategoriesPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminMediaPage from './pages/admin/AdminMediaPage';
import AdminImportPage from './pages/admin/AdminImportPage';
import AdminTeamPage from './pages/admin/AdminTeamPage';
import AdminQRCodesPage from './pages/admin/AdminQRCodesPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/**
 * Root layout — Navbar + page content + Footer.
 */
function RootLayout() {
  return (
    <div className="min-h-screen flex flex-col bg-ui-background">
      <Navbar />
      <div className="flex-1">
        <Outlet />
      </div>
      <Footer />
    </div>
  );
}

/**
 * Home layout — no top padding since the navbar overlays the hero.
 * Other pages get top padding to account for the fixed navbar.
 */
function HomeLayout() {
  return (
    <div className="min-h-screen flex flex-col bg-ui-background">
      <Navbar />
      <div className="flex-1">
        <Outlet />
      </div>
      <Footer />
    </div>
  );
}

function InnerLayout() {
  return (
    <div className="min-h-screen flex flex-col bg-ui-background">
      <div className="flex-1">
        <Outlet />
      </div>
      <Footer />
    </div>
  );
}

const router = createBrowserRouter([
  {
    element: <HomeLayout />,
    children: [
      { path: '/', element: <HomePage /> },
    ],
  },
  {
    element: <InnerLayout />,
    children: [
      { path: '/menu', element: <MenuPage /> },
      { path: '/menu/:slug', element: <ProductDetailPage /> },
    ],
  },
  // Admin auth pages — no sidebar, split layout
  { path: '/admin/login', element: <AdminLoginPage /> },
  { path: '/admin/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/admin/reset-password', element: <ResetPasswordPage /> },
  // Admin protected routes — sidebar layout
  {
    path: '/admin',
    element: (
      <RequireAdmin>
        <AdminLayout />
      </RequireAdmin>
    ),
    children: [
      { index: true, element: <AdminDashboardPage /> },
      { path: 'products', element: <AdminProductsPage /> },
      { path: 'products/new', element: <AdminAddProductPage /> },
      { path: 'orders', element: <AdminOrdersPage /> },
      { path: 'qr-codes', element: <AdminQRCodesPage /> },
      { path: 'import', element: <AdminImportPage /> },
      { path: 'testimonials', element: <AdminTestimonialsPage /> },
      { path: 'team', element: <AdminTeamPage /> },
      { path: 'categories', element: <AdminCategoriesPage /> },
      { path: 'media', element: <AdminMediaPage /> },
      { path: 'settings', element: <AdminSettingsPage /> },
    ],
  },
]);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}

export default App;
